import React, { useCallback, useState } from "react";
import DialogBox from "../DialogBox";
import { locale } from "../../utils/LocaleManager";
import { useContentStore } from "../../stores/contentStore";
import TreeView from "../TreeView";
import { Box, Card, CardContent, Container, Grid, Stack, Alert, Button, Typography } from "@mui/material";
import { moveFolder } from "../../services/folderServices";
import { Bounce, toast } from "react-toastify";
import { useFolderStore } from "../../stores/folderStore";
import { moveFolderRequest } from "../../models/folder";
import FolderIcon from "@mui/icons-material/Folder";
import { handleError } from "../../utils/utilFunctions";
import { useRolePermissionsStore } from "../../stores/rolePermissionsStore";
import { NodeModel } from "@minoru/react-dnd-treeview";

const MoveFolder = () => {
  const { setIsLoading } = useContentStore((state: any) => ({
    setIsLoading: state.setIsLoading,
  }));

  const {
    selectedFolderId,
    previousFolderName,
    setIsShowMoveFolder,
    setIsFolderMoveSuccess,
  } = useFolderStore((state: any) => ({
    selectedFolderId: state.selectedFolderId,
    previousFolderName: state.previousFolderName,
    setIsShowMoveFolder: state.setIsShowMoveFolder,
    setIsFolderMoveSuccess: state.setIsFolderMoveSuccess,
  }));

  const rolePermissions = useRolePermissionsStore(
    (state: any) => state.rolePermissions,
  );

  const [destinationFolderId, setDestinationFolderId] = useState<NodeModel["id"] | null>(null);
  const [destinationFolderName, setDestinationFolderName] = useState<string>("");
  const [inlineError, setInlineError] = useState<string>("");
  const [unavailableDestinationIds, setUnavailableDestinationIds] = useState<NodeModel["id"][]>([]);

  const handleMoveDestinationSelected = useCallback((node: NodeModel) => {
    setDestinationFolderId(node.id);
    setDestinationFolderName(node.text);
  }, []);

  const handleMoveDestinationUnavailableIds = useCallback((ids: NodeModel["id"][]) => {
    setUnavailableDestinationIds((currentIds) =>
      currentIds.length === ids.length &&
      currentIds.every((id, index) => id === ids[index])
        ? currentIds
        : ids,
    );
  }, []);

  const isInvalidDestination =
    destinationFolderId === null ||
    (destinationFolderId === 1 && !rolePermissions?.folder?.root) ||
    (destinationFolderId !== 1 && typeof destinationFolderId !== "string") ||
    destinationFolderId === selectedFolderId ||
    unavailableDestinationIds.includes(destinationFolderId);
  const disableSubmit = !rolePermissions?.folder?.move || isInvalidDestination;

  const fetchMoveFolder = async (id: string, request: moveFolderRequest) => {
    try {
      setIsLoading(true);
      setInlineError("");
      const moveAPI = await moveFolder(id, request);
      if (moveAPI?.status === 200) {
        setIsFolderMoveSuccess(true);
        setIsShowMoveFolder(false);
        toast.success(
          `"${previousFolderName}" folder has been moved to to "${destinationFolderName}"`,
          {
            position: "bottom-left",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            pauseOnFocusLoss: false,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
            onClose: async () => {
              setIsFolderMoveSuccess(false);
            },
          },
        );
      } else {
        const status = moveAPI?.response?.status;
        handleError({
          status,
          toast,
          errorMessage: "",
          onClose: () => {},
        });
        // 403: close dialog and clear transient state
        if (status === 403) {
          setIsShowMoveFolder(false);
        } else if (status !== 401) {
          // Non-401, non-403: show inline error with retry
          setInlineError(`Failed to move folder. Please try again.`);
        }
      }
    } catch (error) {
      console.error("Error moving videos:", error);
      handleError({
        status: 0,
        toast,
        errorMessage: `Error moving folder: ${error}`,
        onClose: () => {},
      });
      // Show inline error with retry for non-HTTP failures
      setInlineError(`Failed to move folder. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const submitMoveFolder = async () => {
    if (disableSubmit) return;

    const request: moveFolderRequest = {
      parentFolderId: destinationFolderId === 1 ? null : destinationFolderId,
    };
    await fetchMoveFolder(selectedFolderId, request);
  };

  const handleRetry = async () => {
    setInlineError("");
    await submitMoveFolder();
  };

  const renderMoveDialog = () => (
    <Container>
      {inlineError && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={handleRetry}>
            Retry
          </Button>
        }>
          {inlineError}
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              width: "100%",
              height: "25rem",
              border: "0.06rem solid #8A8E94",
              borderRadius: "0.5rem",
              overflow: "auto",
            }}
          >
            <Stack>
              <Card variant="outlined" key={selectedFolderId}>
                <CardContent style={{ padding: "1.25rem" }}>
                  <Stack direction="row" gap="0.625rem">
                    <FolderIcon style={{ color: "#444746" }} />
                    {previousFolderName}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              width: "100%",
              height: "25rem",
              border: "0.06rem solid #8A8E94",
              borderRadius: "0.5rem",
              overflow: "auto",
              padding: "0.5rem",
            }}
          >
            <Typography
              data-testid="destination-instruction"
              tabIndex={-1}
              role="status"
              sx={{
                fontWeight: 600,
                fontSize: "0.875rem",
                mb: 1,
                outline: 'none',
              }}
            >
              {locale.destination_instruction}
            </Typography>
            <TreeView
              moveFolderSourceId={selectedFolderId}
              onMoveDestinationUnavailableIds={
                handleMoveDestinationUnavailableIds
              }
              onMoveDestinationSelected={handleMoveDestinationSelected}
              autoFocus
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );

  return (
    <DialogBox
      header={`Move "${previousFolderName}" to "${destinationFolderName || "..."}"`}
      width="1200px"
      body={<></>}
      subBody={renderMoveDialog()}
      onCancel={() => setIsShowMoveFolder(false)}
      onSubmit={submitMoveFolder}
      isDelete={false}
      disablePrimary={disableSubmit}
      primaryButton={locale.control_move}
      secondaryButton={locale.cancel}
    />
  );
};

export default MoveFolder;
